import { Action } from 'redux';
import {
  ClosingDashboardViewMode,
  ClosingStatus,
  DailySale,
  DashboardAutoUpdate,
  DelayedStores,
  HttpErrorBaseAction,
  MastersStatus,
  MasterStatusDetail,
  MonthClosing,
  RemoteResource,
} from '../../types';

export interface State {
  sales: {
    viewMode: ClosingDashboardViewMode;
    closing: RemoteResource<ClosingStatus[]>;
  };
  collections: {
    viewMode: ClosingDashboardViewMode;
    closing: RemoteResource<ClosingStatus[]>;
  };
  closedMonths: {
    accountingMonth: Date;
    closedMonths: RemoteResource<MonthClosing[]>;
  };
  dailySales: {
    delayedStores: RemoteResource<DelayedStores[]>;
    dailySales: RemoteResource<DailySale[]>;
  };
  autoUpdate: DashboardAutoUpdate;
  masterStatus: {
    mastersStatusDetails: RemoteResource<MasterStatusDetail[]>;
  };
  masterStatus2: RemoteResource<MastersStatus[]>;
}

export enum ActionType {
  findClosingSalesStatusRequested = 'dashboard/findClosingSalesStatusRequested',
  findClosingSalesStatusSuccess = 'dashboard/findClosingSalesStatusSuccess',
  findClosingSalesStatusFailure = 'dashboard/findClosingSalesStatusFailure',
  changeSalesViewMode = 'dashboard/changeSalesViewMode',
  findClosingCollectionsStatusRequested = 'dashboard/findClosingCollectionsStatusRequested',
  findClosingCollectionsStatusSuccess = 'dashboard/findClosingCollectionsStatusSuccess',
  findClosingCollectionsStatusFailure = 'dashboard/findClosingCollectionsStatusFailure',
  changeCollectionsViewMode = 'dashboard/changeCollectionsViewMode',
  findClosedMonthsRequested = 'dashboard/findClosedMonthsRequested',
  findClosedMonthsSuccess = 'dashboard/findClosedMonthsSuccess',
  findClosedMonthsFailure = 'dashboard/findClosedMonthsFailure',
  changeAccountingMonth = 'dashboard/changeAccountingMonth',
  changeMonth = 'dashboard/changeMonth',
  findDailySalesRequested = 'dashboard/findDailySalesRequested',
  findDailySalesSuccess = 'dashboard/findDailySalesSuccess',
  findDailySalesFailure = 'dashboard/findDailySalesFailure',
  findDelayedStoresRequested = 'dashboard/findDelayedStoresRequested',
  findDelayedStoresSuccess = 'dashboard/findDelayedStoresSuccess',
  findDelayedStoresFailure = 'dashboard/findDelayedStoresFailure',
  changeAutoUpdate = 'dashboard/changeAutoUpdate',
  findAllRequest = 'dashboard/findAllRequest',
  findMastersStatusMastersStatusDetailsRequested = 'dashboard/findMastersStatusDetailsRequested',
  findMastersStatusMastersStatusDetailsSuccess = 'dashboard/findMastersStatusDetailsSuccess',
  findMastersStatusMastersStatusDetailsFailure = 'dashboard/findMastersStatusDetailsFailure',
  findMastersStatus2Requested = 'dashboard/findMastersStatus2Requested',
  findMastersStatus2Success = 'dashboard/findMastersStatus2Success',
  findMastersStatus2Failure = 'dashboard/findMastersStatus2Failure',
}

export type FindClosingSalesStatusRequestedAction =
  Action<ActionType.findClosingSalesStatusRequested>;

export type FindClosingSalesStatusSuccessAction =
  Action<ActionType.findClosingSalesStatusSuccess> & {
    closing: ClosingStatus[];
  };

export type FindClosingSalesStatusFailureAction =
  HttpErrorBaseAction<ActionType.findClosingSalesStatusFailure>;

export type ChangeSalesViewModeAction = Action<ActionType.changeSalesViewMode> & {
  viewMode: ClosingDashboardViewMode;
};

export type FindClosingCollectionsStatusRequestedAction =
  Action<ActionType.findClosingCollectionsStatusRequested>;

export type FindClosingCollectionsStatusSuccessAction =
  Action<ActionType.findClosingCollectionsStatusSuccess> & {
    closing: ClosingStatus[];
  };

export type FindClosingCollectionsStatusFailureAction =
  HttpErrorBaseAction<ActionType.findClosingCollectionsStatusFailure>;

export type ChangeCollectionsViewModeAction = Action<ActionType.changeCollectionsViewMode> & {
  viewMode: ClosingDashboardViewMode;
};

export type FindClosedMonthsRequestedAction = Action<ActionType.findClosedMonthsRequested>;

export type FindClosedMonthsSuccessAction = Action<ActionType.findClosedMonthsSuccess> & {
  closedMonths: MonthClosing[];
};

export type FindClosedMonthsFailureAction = HttpErrorBaseAction<ActionType.findClosedMonthsFailure>;

export type ChangeAccountingMonthAction = Action<ActionType.changeAccountingMonth> & {
  accountingMonth: Date;
};

export type FindDailySalesRequestedAction = Action<ActionType.findDailySalesRequested>;

export type FindDailySalesSuccessAction = Action<ActionType.findDailySalesSuccess> & {
  dailySales: DailySale[];
};

export type FindDailySalesFailureAction = HttpErrorBaseAction<ActionType.findDailySalesFailure>;

export type FindDelayedStoresRequestedAction = Action<ActionType.findDelayedStoresRequested>;

export type FindDelayedStoresSuccessAction = Action<ActionType.findDelayedStoresSuccess> & {
  delayedStores: DelayedStores[];
};

export type FindDelayedStoresFailureAction =
  HttpErrorBaseAction<ActionType.findDelayedStoresFailure>;

export type ChangeAutoUpdateAction = Action<ActionType.changeAutoUpdate> & {
  autoUpdate: DashboardAutoUpdate;
};

export type FindAllRequestAction = Action<ActionType.findAllRequest>;

export type FindMastersStatusMastersStatusDetailsRequestedAction =
  Action<ActionType.findMastersStatusMastersStatusDetailsRequested> & {
    date: Date;
  };

export type FindMastersStatusMastersStatusDetailsSuccessAction =
  Action<ActionType.findMastersStatusMastersStatusDetailsSuccess> & {
    mastersStatusDetails: MasterStatusDetail[];
  };

export type FindMastersStatusMastersStatusDetailsFailureAction =
  HttpErrorBaseAction<ActionType.findMastersStatusMastersStatusDetailsFailure>;

export type FindMastersStatus2RequestedAction = Action<ActionType.findMastersStatus2Requested>;

export type FindMastersStatus2SuccessAction = Action<ActionType.findMastersStatus2Success> & {
  mastersStatus: MastersStatus[];
};

export type FindMastersStatus2FailureAction =
  HttpErrorBaseAction<ActionType.findMastersStatus2Failure>;

export type Actions =
  | FindClosingSalesStatusRequestedAction
  | FindClosingSalesStatusSuccessAction
  | FindClosingSalesStatusFailureAction
  | ChangeSalesViewModeAction
  | FindClosingCollectionsStatusRequestedAction
  | FindClosingCollectionsStatusSuccessAction
  | FindClosingCollectionsStatusFailureAction
  | ChangeCollectionsViewModeAction
  | FindClosedMonthsRequestedAction
  | FindClosedMonthsSuccessAction
  | FindClosedMonthsFailureAction
  | ChangeAccountingMonthAction
  | FindDailySalesRequestedAction
  | FindDailySalesSuccessAction
  | FindDailySalesFailureAction
  | FindDelayedStoresRequestedAction
  | FindDelayedStoresSuccessAction
  | FindDelayedStoresFailureAction
  | ChangeAutoUpdateAction
  | FindMastersStatusMastersStatusDetailsRequestedAction
  | FindMastersStatusMastersStatusDetailsSuccessAction
  | FindMastersStatusMastersStatusDetailsFailureAction
  | FindMastersStatus2RequestedAction
  | FindMastersStatus2SuccessAction
  | FindMastersStatus2FailureAction;
